import { makeAutoObservable, runInAction } from 'mobx';
import { api } from '@/api';
import { setSessionTokenProvider, setSessionTokenListener } from '@/api/apiClient';
import type { RootStore } from './RootStore';
import type { AvatarColor } from '@/components/ui/Avatar';

const LS_KEY = 'skvoz:auth';

type Persisted = { playerId: string; sessionToken: string; nickname: string; avatarColor?: AvatarColor };

export type AuthMode = 'guest' | 'telegram' | null;

const AVATAR_COLORS: AvatarColor[] = ['l1', 'l2', 'l3', 'accent'];

function defaultAvatarColor(nickname: string): AvatarColor {
  let h = 0;
  for (let i = 0; i < nickname.length; i++) h = (h * 31 + nickname.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

export class AuthStore {
  playerId: string | null = null;
  sessionToken: string | null = null;
  nickname: string | null = null;
  avatarColor: AvatarColor = 'accent';
  mode: AuthMode = null;
  isRegistering = false;
  error: string | null = null;

  constructor(_root: RootStore) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.restoreFromStorage();
    setSessionTokenProvider(() => this.sessionToken);
    setSessionTokenListener((token) => this.setSessionToken(token));
  }

  get isAuthenticated(): boolean {
    return this.playerId !== null;
  }

  // Any response carrying a `sessionToken` (register/create/join) routes here via
  // apiClient's side-channel listener, regardless of which page triggered the call.
  setSessionToken(token: string): void {
    runInAction(() => {
      this.sessionToken = token;
    });
    this.persist();
  }

  private restoreFromStorage() {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return;
    try {
      const data = JSON.parse(raw) as Partial<Persisted>;
      // Pre-SEC-A records (or anything missing a token) can't authenticate anymore —
      // treat them as logged out rather than restoring a playerId with no credential.
      if (!data.playerId || !data.nickname || !data.sessionToken) {
        localStorage.removeItem(LS_KEY);
        return;
      }
      this.playerId = data.playerId;
      this.sessionToken = data.sessionToken;
      this.nickname = data.nickname;
      this.avatarColor = data.avatarColor ?? defaultAvatarColor(data.nickname);
      this.mode = 'guest';
    } catch {
      localStorage.removeItem(LS_KEY);
    }
  }

  private persist() {
    if (this.playerId && this.nickname && this.sessionToken) {
      const data: Persisted = {
        playerId: this.playerId,
        sessionToken: this.sessionToken,
        nickname: this.nickname,
        avatarColor: this.avatarColor,
      };
      localStorage.setItem(LS_KEY, JSON.stringify(data));
    }
  }

  async registerGuest(nickname: string): Promise<void> {
    const trimmed = nickname.trim();
    if (trimmed.length < 1 || trimmed.length > 24) {
      this.error = 'Ник: от 1 до 24 символов';
      throw new Error(this.error);
    }
    this.isRegistering = true;
    this.error = null;
    try {
      const res = await api.players.registerGuest(trimmed);
      runInAction(() => {
        this.playerId = res.playerId;
        this.sessionToken = res.sessionToken;
        this.nickname = res.nickname;
        this.avatarColor = defaultAvatarColor(res.nickname);
        this.mode = 'guest';
        this.isRegistering = false;
      });
      this.persist();
    } catch (e) {
      runInAction(() => {
        this.isRegistering = false;
        this.error = e instanceof Error ? e.message : 'Ошибка регистрации';
      });
      throw e;
    }
  }

  async authenticateTelegramMiniApp(initData: string): Promise<void> {
    this.isRegistering = true;
    this.error = null;
    try {
      const res = await api.auth.telegramMiniApp(initData);
      runInAction(() => {
        this.playerId = res.playerId;
        this.nickname = res.nickname;
        this.mode = 'telegram';
        this.isRegistering = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isRegistering = false;
        this.error = e instanceof Error ? e.message : 'Ошибка авторизации';
      });
      throw e;
    }
  }

  signOut(): void {
    this.playerId = null;
    this.sessionToken = null;
    this.nickname = null;
    this.avatarColor = 'accent';
    this.mode = null;
    localStorage.removeItem(LS_KEY);
  }
}
